import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { BullseyeLineIcon } from '@/components/icons/bullseye-line';
import { ExclamationIcon } from '@/components/icons/exclamation';
import { TrendingUpIcon } from '@/components/icons/trending-up';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

function SnapshotsContent() {
  const intl = useIntl();

  const {
    data: { snapshots },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.dashboard.snapshots],
    queryFn: () => SourcingService.getInternalApiSourcingDashboardSnapshots(),
  });

  const content = useMemo(
    () => ({
      sourcing: {
        count: snapshots.active_sourcing_openings,
        title: intl.formatMessage({
          defaultMessage: 'Active Sourcing',
          description:
            'Label for job openings currently being actively recruited',
        }),
        icon: <TrendingUpIcon className="text-fx-primary size-6" />,
        backgroundColor: 'bg-fx-brand-50',
      },
      atRisk: {
        count: snapshots.openings_by_status.at_risk,
        title: intl.formatMessage({
          defaultMessage: 'At Risk',
          description:
            'Status label for job openings unlikely to reach their hiring goal',
        }),
        icon: <ExclamationIcon className="text-fx-amber size-6" />,
        backgroundColor: 'bg-fx-amber-50',
      },
      onTrack: {
        count:
          snapshots.openings_by_status.on_track +
          snapshots.openings_by_status.in_progress,
        title: intl.formatMessage({
          defaultMessage: 'On Track / In Progress',
          description:
            'Status label for job openings progressing toward their hiring goal',
        }),
        icon: <BullseyeLineIcon className="text-fx-primary size-6" />,
        backgroundColor: 'bg-fx-brand-50',
      },
    }),
    [intl, snapshots],
  );

  return (
    <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-3">
      {Object.values(content).map(item => (
        <div
          key={item.title}
          className="border-fx-neutral-200 flex items-center gap-4 rounded-md border bg-white p-2"
        >
          {/* Icon */}
          <div
            className={clsx(
              'flex size-10 items-center justify-center rounded-md p-2',
              item.backgroundColor,
            )}
          >
            {item.icon}
          </div>

          <div className="flex flex-col">
            {/* Title */}
            <Typography variant="subtitle1" className="!text-fx-neutral-600">
              {item.title}
            </Typography>

            {/* Count */}
            <Typography variant="h3">{item.count ?? 0}</Typography>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Snapshots() {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<SnapshotsContent.Skeleton />}>
        <SnapshotsContent />
      </Suspense>
    </ErrorBoundary>
  );
}

SnapshotsContent.Skeleton = function SnapshotContentSkeleton() {
  return (
    <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-3">
      {_range(3).map(index => (
        <div
          key={index}
          className="border-fx-neutral-200 flex items-center gap-4 rounded-md border bg-white p-2"
        >
          <Skeleton className="size-10 !rounded-md" />

          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-25" />
            <Skeleton className="h-6 w-8" />
          </div>
        </div>
      ))}
    </div>
  );
};
